import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

import { Neutrals, SemanticError } from '../../color-palette/colors';

import type { FieldSize } from './field.types';

export const staticLabelSx = (theme: Theme): SystemStyleObject<Theme> => ({
  position: 'relative',
  transform: 'none',
  maxWidth: 'none',
  whiteSpace: 'normal',
  pointerEvents: 'auto',
  marginBottom: theme.spacing(1),
  ...theme.typography.label,
  fontWeight: theme.typography.fontWeightMedium,
  color: Neutrals[700],
  '&.Mui-focused': { color: Neutrals[700] },
  '&.Mui-error': { color: SemanticError[600] },
  '&.Mui-disabled': { color: Neutrals[400] },
});

export const visuallyHiddenSx: SystemStyleObject<Theme> = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
};

const SINGLE_LINE_INPUT = '& .MuiInputBase-input:not(.MuiInputBase-inputMultiline):not(.MuiAutocomplete-input)';
const OUTLINE = '.MuiOutlinedInput-notchedOutline';
const PLACEHOLDER = Neutrals[600];

export const outlineSx = (theme: Theme): SystemStyleObject<Theme> => ({
  [`& ${OUTLINE}`]: { borderColor: Neutrals[500] },
  [`&:hover ${OUTLINE}`]: { borderColor: Neutrals[700] },
  [`&.Mui-focused ${OUTLINE}`]: { borderColor: theme.palette.primary.main },
  [`&.Mui-error ${OUTLINE}, &.Mui-error.Mui-focused ${OUTLINE}`]: { borderColor: SemanticError[600] },
  [`&.Mui-disabled ${OUTLINE}`]: { borderColor: theme.palette.action.disabled },
});

export const controlTextSx = (size: FieldSize) => (theme: Theme): SystemStyleObject<Theme> => ({
  fontSize: size === 'small' ? theme.typography.h4.fontSize : theme.typography.body.fontSize,
  minHeight: theme.controlHeight[size],
  [SINGLE_LINE_INPUT]: {
    height: theme.controlHeight[size],
    paddingTop: 0,
    paddingBottom: 0,
    boxSizing: 'border-box',
  },
  '@media (pointer: coarse)': {
    minHeight: theme.controlHeight.medium,
    fontSize: theme.typography.body.fontSize,
    [SINGLE_LINE_INPUT]: { height: theme.controlHeight.medium },
  },
  ...outlineSx(theme),
  '& .MuiSelect-select': { display: 'flex', alignItems: 'center' },
  '& ::placeholder': { color: PLACEHOLDER, opacity: 1 },
  '& input[type="search"]::-webkit-search-cancel-button': { WebkitAppearance: 'none', appearance: 'none', display: 'none' },
  '& input[type="search"]::-webkit-search-decoration': { WebkitAppearance: 'none', appearance: 'none' },
  '& input[type="search"]::-ms-clear': { display: 'none' },
});

export const placeholderSx: SystemStyleObject<Theme> = { color: PLACEHOLDER };

export const adornmentSx: SystemStyleObject<Theme> = { color: Neutrals[600], '& .MuiTypography-root': { color: Neutrals[600] } };

export const hiddenRequiredMarkSx: SystemStyleObject<Theme> = { '& .MuiFormLabel-asterisk': { display: 'none' } };
